/**
 * App's entry point
 */
import './globals.css';

// polyfills
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { FlagProvider } from '@unleash/proxy-client-react';
import initWave from '@volue/wave';
import {
  createToastManager,
  Toaster,
  useToastManager
} from '@volue/wave-react';
import { createOvermind } from 'overmind';
import { Provider } from 'overmind-react';
import React from 'react';
import { createRoot } from 'react-dom/client';

import type { CalculationState } from '@/features/execute-calculation-set/overmind/calculation';

import App from './app/components/App';
import { ROOT_ELEMENT_ID } from './common/constants';
import { config, useAppState } from './overmind';
import { unleashClient } from './overmind/effects/feature-toggles';

const GLOBAL_TOAST_ID = Symbol('GLOBAL_TOAST_ID');
const AppNotification = () => {
  const toastManager = useToastManager();
  const notification = useAppState().calculation.calculationTacticNotification;

  if (notification.type === 'NO_NOTIFICATION') {
    toastManager.removeToast(GLOBAL_TOAST_ID.toString());

    return null;
  }
  const notificationTypeToToastMapper: Record<
    CalculationState['calculationTacticNotification']['type'],
    () => void
  > = {
    NO_NOTIFICATION: () => {},
    FAILURE_NOTIFICATION: () =>
      toastManager.addToast({
        message: notification.notificationText,
        id: GLOBAL_TOAST_ID.toString(),
        type: 'error',
        isClosable: false
      }),
    SUCCESSFUL_NOTIFICATION: () =>
      toastManager.addToast({
        message: notification.notificationText,
        id: GLOBAL_TOAST_ID.toString(),
        type: 'success',
        isClosable: false
      })
  };

  notificationTypeToToastMapper[notification.type]();

  return null;
};

initWave();
const root = createRoot(
  document.getElementById(ROOT_ELEMENT_ID) as HTMLElement
);

const overmind = createOvermind(config, { devtools: !__PROD__ });

const toasterManager = createToastManager();

root.render(
  <React.StrictMode>
    <Provider value={overmind}>
      {/** @ts-ignore */}
      <FlagProvider unleashClient={unleashClient} startClient={false}>
        <Toaster manager={toasterManager} position="topCenter">
          <AppNotification />
          <App />
        </Toaster>
      </FlagProvider>
    </Provider>
  </React.StrictMode>
);

// When this module runs again "createOvermind" is run
// and it automatically reconfigures
if (__DEV__ && module?.hot) {
  module.hot.accept();
}
